/**
 * Utility functions for consistent error handling across the frontend
 */

/**
 * Extract a user-friendly error message from an axios error
 * @param {Error} error - The error object from axios
 * @returns {string} - A user-friendly error message
 */
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Check if the error is an authentication error (401 or token failure)
 * @param {Error} error - The error object from axios
 * @returns {boolean} - True if it's an auth error
 */
export const isAuthError = (error) => {
  if (error.response?.status === 401) {
    return true;
  }
  const message = error.response?.data?.message || '';
  return message.includes('Not authorized') || message.includes('token failed');
};

/**
 * Handle API errors consistently
 * Logs to console only in development, handles auth errors, and returns the message
 * @param {Error} error - The error object from axios
 * @param {Function} logUserOut - Optional callback to log the user out on auth errors
 * @returns {string} - The error message for display
 */
export const handleApiError = (error, logUserOut = null) => {
  const message = getErrorMessage(error);

  // Log errors only in development
  if (process.env.NODE_ENV === 'development') {
    console.error('API Error:', error);
  }

  // Handle authentication errors
  if (isAuthError(error) && logUserOut) {
    logUserOut();
  }

  return message;
};

/**
 * Create axios config with authorization header
 * @param {string} token - The JWT token
 * @returns {Object} - Axios config object
 */
export const createAuthConfig = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});
